import React, { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useRouter } from '@blocs.router';
import Modal from '@minecraft.modal';
import { useTranslation } from '@blocs.i18n';
import { BodyText, FlexDynamic } from '@minecraft.atoms';
import { captureExceptionEvent } from '@blocs.observability';
import { WindowWidthContext } from '@minecraft.utils';
import { useRouteHelper } from '@consumer-talent.utils/routeHelper';
import { gqlProfileStatusEnum } from '@minecraft.graphql-types';
import { useArtistContext } from './ArtistContext';
import { useCreditsUI } from './useCreditsUI';
import { MediaPersistentBanner } from './MediaPersistentBanner';

const SESSION_STORAGE_KEY = 'creditPopupShown';

export const PageCreditsUI = React.memo(() => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { pathname: path } = useRouter();
  const { t } = useTranslation();
  const { currentArtist, profileStatusCode } = useArtistContext();
  const { artistId } = currentArtist || {};
  const isMobile = WindowWidthContext.useIsMobile();
  const { push } = useRouteHelper();
  const { loading, userHasCreditsToRedeem, priorityMediaToRedeem, availableCreditsByType } = useCreditsUI({
    artistId,
  });
  const routesToShow = [
    '/casting-billboard',
    '/profile/:profileId',
    '/media-bank/:mediaType',
    '/project/:projectId',
    '/project/:projectId/role/:roleId/customize-submission',
    '/project/:projectId/role/:roleId',
  ];
  const isRouteToShow = routesToShow.some((route) => matchPath(path, { path: route, exact: true, strict: false }));
  const isMediaRoute = !!matchPath(path, {
    path: '/media-bank/:mediaType',
    exact: true,
    strict: false,
  });

  const toggleModal = () => setIsModalOpened(!isModalOpened);

  useEffect(() => {
    if (isRouteToShow && !loading && userHasCreditsToRedeem && profileStatusCode === gqlProfileStatusEnum.ACTIVE) {
      try {
        const sessionObj = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}');
        const hasShownModal = sessionObj[artistId];
        if (!hasShownModal) {
          setIsModalOpened(true);
          sessionObj[artistId] = true;
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
        }
      } catch (err) {
        captureExceptionEvent(err);
        sessionStorage.setItem(SESSION_STORAGE_KEY, '{}');
      }
    }
  }, [artistId, isRouteToShow, loading, profileStatusCode, userHasCreditsToRedeem]);

  return (
    <>
      {userHasCreditsToRedeem && profileStatusCode === gqlProfileStatusEnum.ACTIVE && (
        <MediaPersistentBanner
          priorityMediaToRedeem={priorityMediaToRedeem}
          isMediaRoute={isMediaRoute}
          availableCreditsByType={availableCreditsByType}
        />
      )}
      <Modal
        isOpen={isModalOpened}
        title={t('talent:creditPopup.title')}
        handleToggleClick={toggleModal}
        data-qa-id="media-migration-credit-modal"
        data-testid="media-migration-credit-modal"
        modalSize="small"
        customHeaderPadding={{ left: 2, bottom: 2, right: 2, top: 2, mdProps: { left: 0 } }}
        bodyProps={{ className: `${isMobile ? 'cn_atom_py-6 cn_atom_px-3' : 'cn_atom_py-1 cn_atom_px-8 '}` }}
        buttons={[
          {
            label: t('common:button.cancel'),
            onClick: (): void => {
              setIsModalOpened(false);
            },
            variant: 'secondary',
          },
          {
            label: t('talent:creditPopup.cta'),
            onClick: (): void => {
              !isMediaRoute && push('mediaBank', priorityMediaToRedeem);
              setIsModalOpened(false);
            },
          },
        ]}
      >
        <FlexDynamic direction="column" mdProps={{ mb: '2' }} mb="28">
          <BodyText>{t('talent:creditPopup.description')}</BodyText>
        </FlexDynamic>
      </Modal>
    </>
  );
});
